import h5Login from '@hb/h5-login';
import { env } from '@hb/mbridge';
import nativeMgr from '../native/native';
import { StorageMgr, storageMgr } from '../storageMgr';
import logger from '../utils/logger';
import {
  isHelloFamilyApp,
} from '../env/env';
import type { UserInfo } from '../../model/store/modules/user';

export interface LoginOptions {
  operatorLogin?: boolean;
  noWechatLogin?: boolean;
  operatorLoginClosable?: boolean;
  forceRefresh?: boolean; // 强制刷新登录状态
}

export interface LoginResult {
  success: boolean;
  userInfo?: UserInfo;
  error?: string;
  errorCode?: string; // 交银小程序授权错误码，如 XP1100
}

interface ILoginManager {
  signIn(options?: LoginOptions): Promise<LoginResult>;
  signOut(): Promise<void>;
  checkLoginStatus(): Promise<boolean>;
  relogin(): Promise<LoginResult>;
}

/**
 * 统一的登录管理器
 * 整合了所有登录相关的逻辑，包括原生登录、H5登录、缓存管理等
 */
class LoginManager implements ILoginManager {
  private static instance: ILoginManager | undefined;

  // 默认登录配置
  private defaultOptions: LoginOptions = {
    operatorLogin: true,
    noWechatLogin: true,
    operatorLoginClosable: false,
    forceRefresh: false,
  };

  static getInstance(): ILoginManager {
    if (!LoginManager.instance) {
      LoginManager.instance = new LoginManager();
    }
    return LoginManager.instance;
  }

  /**
   * 统一登录方法
   * @param options 登录选项
   * @returns 登录结果
   */
  async signIn(options: LoginOptions = {}): Promise<LoginResult> {
    const finalOptions = { ...this.defaultOptions, ...options };

    try {
      logger('🔐 开始登录流程', finalOptions);

      // 1. 如果不强制刷新，先尝试获取缓存的用户信息
      if (!finalOptions.forceRefresh) {
        const cachedUserInfo = await this.getCachedUserInfo();
        if (cachedUserInfo && cachedUserInfo.token) {
          logger('✅ 使用缓存用户信息', cachedUserInfo);
          return {
            success: true,
            userInfo: cachedUserInfo,
          };
        }
      }

      // 3. 如果是HelloFamily或支付宝小程序环境，尝试原生登录
      if (isHelloFamilyApp || env.isAliMP) {
        logger('✅ 原生登录？', env);
        const nativeUserInfo = await this.tryNativeLogin();
        if (nativeUserInfo && nativeUserInfo.token) {
          logger('✅ 原生登录成功', nativeUserInfo);
          this.saveUserInfo(nativeUserInfo);
          return {
            success: true,
            userInfo: nativeUserInfo,
          };
        }
      }

      // 3. 使用H5登录
      const h5UserInfo = await this.tryH5Login(finalOptions);
      if (h5UserInfo && h5UserInfo.token) {
        logger('✅ H5登录成功', h5UserInfo);
        this.saveUserInfo(h5UserInfo);
        return {
          success: true,
          userInfo: h5UserInfo,
        };
      }

      // 5. 所有登录方式都失败
      return {
        success: false,
        error: '登录失败：所有登录方式都不可用',
      };
    } catch (error) {
      logger('❌ 登录失败', error);
      return {
        success: false,
        error: (error as Error).message || '登录过程中发生未知错误',
        userInfo: undefined,
      };
    }
  }

  /**
   * 获取缓存的用户信息
   */
  private async getCachedUserInfo(): Promise<UserInfo | null> {
    try {
      // 1. 优先从Storage获取
      const storageUserInfo = storageMgr.getItem(StorageMgr.USERINFO);
      if (storageUserInfo && storageUserInfo.token) {
        logger('📦 从Storage获取用户信息', storageUserInfo);
        return storageUserInfo;
      }

      // 2. 检查中行App的兼容性缓存
      const marketData = storageMgr.getItem(StorageMgr.MARKETINFO);
      if (marketData?.val?.token) {
        logger('📦 从MarketInfo获取用户信息（中行兼容）', marketData.val);
        return marketData.val;
      }

      return null;
    } catch (error) {
      logger('⚠️ 获取缓存用户信息失败', error);
      return null;
    }
  }

  /**
   * 尝试原生登录
   */
  private async tryNativeLogin(): Promise<UserInfo | null> {
    try {
      logger('🔄 尝试原生登录');

      // 检查是否为中行App（特殊处理）
      const u = navigator.userAgent;
      const isBoc = u.indexOf('bocapp(') > -1;

      if (isBoc) {
        logger('ℹ️ 检测到中行App，跳过原生登录');
        return null;
      }

      const userInfo = await nativeMgr.getUserInfo();
      if (userInfo && userInfo.token) {
        logger('✅ 原生登录获取到用户信息', userInfo);
        return userInfo as UserInfo;
      }

      return null;
    } catch (error) {
      logger('⚠️ 原生登录失败', error);
      return null;
    }
  }

  /**
   * 尝试H5登录
   */
  private async tryH5Login(options: LoginOptions): Promise<UserInfo | null> {
    try {
      logger('🌐 开始H5登录', options);

      const userInfo = await h5Login.signIn({
        operatorLogin: options.operatorLogin,
        noWechatLogin: options.noWechatLogin,
        operatorLoginClosable: options.operatorLoginClosable,
      });

      if (!userInfo || !userInfo.token) {
        throw new Error('H5登录返回的用户信息无效');
      }

      logger('✅ H5登录成功', userInfo);
      return userInfo as UserInfo;
    } catch (error) {
      logger('❌ H5登录失败', error);

      // 检查是否为跳转到登录页的错误
      if ((error as Record<string, unknown>)?.goToLogin) {
        logger('🔄 正在跳转到登录页');
      }

      throw error;
    }
  }

  /**
   * 保存用户信息到缓存
   */
  private saveUserInfo(userInfo: UserInfo) {
    try {
      if (userInfo && userInfo.token) {
        storageMgr.setItem(StorageMgr.USERINFO, userInfo);
        logger('💾 用户信息已保存到缓存', userInfo);
      }
    } catch (error) {
      logger('⚠️ 保存用户信息失败', error);
    }
  }

  /**
   * 登出方法
   */
  async signOut(): Promise<void> {
    try {
      logger('🔓 开始登出流程');

      // 清除缓存的用户信息
      storageMgr.removeItem(StorageMgr.USERINFO);
      storageMgr.removeItem(StorageMgr.MARKETINFO);

      logger('✅ 登出完成');
    } catch (error) {
      logger('❌ 登出失败', error);
    }
  }

  /**
   * 检查登录状态
   */
  async checkLoginStatus(): Promise<boolean> {
    try {
      const userInfo = await h5Login.initUserInfo();
      console.log('userInfo', userInfo);
      return !!(userInfo?.token);
    } catch (error) {
      logger('❌ 检查登录状态失败', error);
      return false;
    }
  }

  /**
   * 强制重新登录
   */
  async relogin(): Promise<LoginResult> {
    logger('🔄 强制重新登录？？？');
    storageMgr.removeItem(StorageMgr.USERINFO);
    return this.signIn({ forceRefresh: true });
  }
}

// 导出 LoginManager 类供类型使用
export { LoginManager };

// 导出单例实例
export const loginManager = LoginManager.getInstance();
